import React, { useEffect, useState } from 'react'
import { NextPage } from 'next'
import {
  Background,
  Content,
  FormContainer,
  Pointers,
  Step,
  StepContainer,
} from 'src/components/Forgot/styles'
import { FormProvider } from 'src/components/Common/Form'
import initialValues, { Values } from 'src/shapes/forgot'
import { Button } from 'src/components/Common/Button'
import Container from 'src/components/Common/Container'
import { FormikProps, useFormik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import StepOne from 'src/components/Forgot/StepOne'
import StepTwo from 'src/components/Forgot/StepTwo'
import StepThree from 'src/components/Forgot/StepThree'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import { apiPrivate } from 'src/service/api/api.private'
import { useDispatch } from 'react-redux'
import { useErrors } from 'src/hooks/useErrors'
import { setError } from 'src/store/errors'
import Turnstile, { useTurnstile } from 'react-turnstile'
import isLogged from 'src/utils/isLogged'

interface StepToAction {
  [key: number]: (formik: FormikProps<Values>) => Promise<void>
}

const Forgot: NextPage = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const [step, setStep] = React.useState(1)
  const [isLoading, setLoading] = React.useState(false)

  const turnstile = useTurnstile()
  const [turnstileToken, setTurnstileToken] = useState<string>('')

  const dispatch = useDispatch()

  useErrors()

  useEffect(() => {
    if (isLogged()) {
      window.location.replace('/perfil')
    }
  }, [])

  const stepToAction: StepToAction = {
    1: async formik => {
      const errors = await formik.validateForm()

      formik.validateField('email')
      formik.setFieldTouched('email')

      if (!errors.email) {
        await handleStepOne(formik.values.email)
      }

      setLoading(false)
    },
    2: async formik => {
      const errors = await formik.validateForm()

      if (!errors.token) {
        await handleStepTwo(formik.values.token)
      }

      setLoading(false)
    },
    3: async formik => formik.handleSubmit(),
  }

  const handleNextStep = async (formik: FormikProps<Values>) => {
    setLoading(true)
    const action = stepToAction[step]
    await action(formik)
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async values => {
      await handleStepThree(values.token, values.newPassword, values.newPasswordConfirmation)
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email(t('validation.email.matches')).required(t('validation.required')),
      token: Yup.string().required(t('validation.required')),
      newPassword: Yup.string()
        .min(8, t('validation.password.min'))
        .required(t('validation.required')),
      newPasswordConfirmation: Yup.string()
        .required(t('validation.required'))
        .when('newPassword', (newPassword: string, schema) =>
          schema.test({
            test: (repeatNewPassword: string) => repeatNewPassword === newPassword,
            message: t('validation.password.diff'),
          }),
        ),
    }),
  })

  async function handleStepOne(email: string) {
    if (!turnstileToken || !process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY) {
      // eslint-disable-next-line no-console
      console.error('Turnstile token not available')
      return
    }

    const res = await apiPrivate.sendRecoveryCode({ payload: { email }, captcha: turnstileToken })

    if (res.kind === 'ok') {
      setStep(2)
    } else {
      dispatch(
        setError({
          response: res,
        }),
      )

      turnstile.reset()
    }
  }

  async function handleStepTwo(token: string) {
    const res = await apiPrivate.verifyCode({ token })

    if (res.kind === 'ok') {
      setStep(3)
    } else {
      dispatch(
        setError({
          response: res,
          customMessages: {
            unauthorized: 'toast.invalidRecoveryCode',
          },
        }),
      )
    }
  }

  async function handleStepThree(
    token: string,
    newPassword: string,
    newPasswordConfirmation: string,
  ) {
    const res = await apiPrivate.resetPassword({ token, newPassword, newPasswordConfirmation })

    if (res.kind === 'ok') {
      toast(t('toast.resetPasswordSuccess'), { type: 'success' })
      router.replace('/login')
    } else {
      dispatch(
        setError({
          response: res,
        }),
      )
    }

    setLoading(false)
  }

  const textButton = {
    1: () => t('forgot.buttons.one'),
    2: () => t('forgot.buttons.two'),
    3: () => t('forgot.buttons.three'),
  }[step]()

  const moveStep = {
    1: () => '0%',
    2: () => '-100%',
    3: () => '-200%',
  }[step]()

  const setActive = (position: number) => (step === position ? 'active' : '')
  const previousStep = () => (step === 1 ? router.replace('/login') : setStep(step - 1))
  const textBackButton = step === 1 ? t('forgot.buttons.backLogin') : t('backButton')

  return (
    <Background>
      <Container>
        <Content>
          <FormContainer>
            <div className="form-content">
              <Pointers>
                <div className={`pointer ${setActive(1)}`}>
                  <span>1</span>
                </div>
                <div className={`pointer ${setActive(2)}`}>
                  <span>2</span>
                </div>
                <div className={`pointer ${setActive(3)}`}>
                  <span>3</span>
                </div>
              </Pointers>
              <FormProvider<Values> provider={formik}>
                <StepContainer num={moveStep}>
                  <Step>
                    <StepOne
                      onTurnstileVerify={token => setTurnstileToken(token)}
                      onTurnstileError={() => setTurnstileToken('')}
                      onTurnstileExpire={() => setTurnstileToken('')}
                    />
                  </Step>

                  <Step>
                    <StepTwo />
                  </Step>

                  <Step>
                    <StepThree />
                  </Step>
                </StepContainer>

                <Button
                  onClick={() => handleNextStep(formik)}
                  isLoading={isLoading}
                  type="button"
                  preset="form"
                  labelTx={textButton}
                />
                <Button
                  onClick={() => previousStep()}
                  type="button"
                  preset="transparent"
                  label={textBackButton}
                />
              </FormProvider>
            </div>
          </FormContainer>
        </Content>
      </Container>
    </Background>
  )
}

export default Forgot
